import { useEffect, useState } from "react";
import { adminKpiApiService } from "../../../../infrastructure/api/admin/admin-kpi-api-service";
import {
  graphSeriesLabel,
  type ComputeResponse,
  type KpiInterval,
  type KpiMachineScope,
} from "../../../../domain/types/company/company-kpi";
import { ComputeChart } from "../../CompanyAdmin/KpiCards/ComputeChart";
import { SBIZ_TYPE_LABELS, KPI_TYPE_LABELS, type KpiSlot } from "../../../../domain/types/kpi";

const INTERVALS: { v: KpiInterval; l: string }[] = [
  { v: "DAY", l: "일" },
  { v: "WEEK", l: "주" },
  { v: "MONTH", l: "월" },
];

/** 슬롯 1개 추이 차트 — 어드민 compute(companyId + sbizType + 슬롯 측정 장비 합산)로 계산. */
const SlotChart = ({
  companyId,
  slot,
  tier,
  targetMachineId,
}: {
  companyId: number;
  slot: KpiSlot;
  tier: 1 | 2;
  targetMachineId: number | null;
}) => {
  const [interval, setInterval] = useState<KpiInterval>("DAY");
  const [data, setData] = useState<ComputeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const label = slot.sbizType != null ? SBIZ_TYPE_LABELS[slot.sbizType] : KPI_TYPE_LABELS[slot.kpiType];

  // 슬롯에 등록된 측정 장비(합산) 전체를 그래프에 반영. 없으면 대표장비 1대로 폴백.
  const slotMachineIds = (slot.machines ?? []).map((m) => m.id);
  const computeMachineIds =
    slotMachineIds.length > 0
      ? slotMachineIds
      : targetMachineId != null
        ? [targetMachineId]
        : undefined;
  const machineKey = (computeMachineIds ?? []).join(",");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await adminKpiApiService.compute(companyId, {
          interval,
          periodPreset: "30D",
          metrics: [
            {
              label: graphSeriesLabel(slot.sbizType ?? slot.kpiType, label),
              sbizType: slot.sbizType ?? undefined,
              kpiType: slot.sbizType == null ? slot.kpiType : undefined,
              machineScope: (computeMachineIds ? "SPECIFIC" : "ALL") as KpiMachineScope,
              machineIds: computeMachineIds,
            },
          ],
        });
        if (alive) setData(res);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : "계산에 실패했습니다.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, slot.sbizType, slot.kpiType, machineKey, interval]);

  return (
    <div className="border border-[#eee] rounded-[12px] p-4 bg-white">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold text-[#4285F4]">KPI {tier}</span>
          <span className="text-[14px] font-medium text-[#222]">{label}</span>
        </div>
        <div className="inline-flex items-center rounded-[6px] border border-[#e0e0e0] overflow-hidden">
          {INTERVALS.map((o) => (
            <button
              key={o.v}
              onClick={() => setInterval(o.v)}
              className={`px-2 py-1 text-[11px] border-r border-[#e0e0e0] last:border-r-0 transition-colors ${
                interval === o.v
                  ? "bg-[#4285F4] text-white"
                  : "bg-white text-[#666] hover:bg-[#F5F5F5]"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="h-[220px] flex items-center justify-center text-[13px] text-[#888]">
          불러오는 중…
        </div>
      ) : error ? (
        <div className="h-[220px] flex items-center justify-center text-[13px] text-[#F24A4A]">
          {error}
        </div>
      ) : data ? (
        <ComputeChart response={data} chartType="LINE" height={220} />
      ) : null}
    </div>
  );
};

/** 미등록 자리 placeholder — 위 슬롯 카드의 좌/우 위치와 정렬 유지. */
const EmptyCell = ({ tier }: { tier: 1 | 2 }) => (
  <div className="border border-dashed border-[#e0e0e0] rounded-[12px] p-4 bg-[#FAFAFA] flex flex-col">
    <div className="text-[12px] font-bold text-[#bbb] mb-2">KPI {tier}</div>
    <div className="h-[220px] flex items-center justify-center text-[12px] text-[#bbb]">
      지표 미등록
    </div>
  </div>
);

/**
 * 보고 KPI 슬롯 아래 동적 추이 그래프.
 * 위 슬롯 카드와 동일하게 KPI 1(P)=왼쪽 / KPI 2(D·Q)=오른쪽 고정 위치.
 */
export const AdminKpiTrendSection = ({
  companyId,
  kpi1Slot,
  kpi2Slot,
  targetMachineId,
}: {
  companyId: number;
  kpi1Slot: KpiSlot | null;
  kpi2Slot: KpiSlot | null;
  targetMachineId: number | null;
}) => {
  if (!kpi1Slot && !kpi2Slot) return null;
  return (
    <div className="mt-6">
      <div className="text-[14px] font-bold text-[#222] mb-3">KPI 추이</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {kpi1Slot ? (
          <SlotChart companyId={companyId} slot={kpi1Slot} tier={1} targetMachineId={targetMachineId} />
        ) : (
          <EmptyCell tier={1} />
        )}
        {kpi2Slot ? (
          <SlotChart companyId={companyId} slot={kpi2Slot} tier={2} targetMachineId={targetMachineId} />
        ) : (
          <EmptyCell tier={2} />
        )}
      </div>
    </div>
  );
};